<div class="w-full mx-auto p-5 space-y-6">

  <!-- Header -->
  <div class="flex flex-wrap justify-between items-center gap-4">
    <div>
      <h1 class="text-2xl font-bold text-red-800">⚠️ Reseteo del Sistema</h1>
      <p class="text-gray-500 mt-1">Herramienta para vaciar completamente todos los datos del sistema financiero</p>
    </div>
    <button 
      *ngIf="showSimulation || showResetForm || showResults"
      (click)="resetView()"
      class="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition flex items-center gap-2">
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
      Volver al Inicio
    </button>
  </div>

  <!-- Advertencia Principal -->
  <div class="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
    <div class="flex">
      <div class="flex-shrink-0">
        <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
      </div>
      <div class="ml-3">
        <h3 class="text-sm font-medium text-red-800">Advertencia Crítica</h3>
        <div class="mt-2 text-sm text-red-700">
          <ul class="list-disc list-inside space-y-1">
            <li>Esta herramienta eliminará <strong>TODOS</strong> los datos del sistema de manera permanente</li>
            <li>No es posible recuperar los datos una vez ejecutado el reseteo</li>
            <li>Solo debe ser utilizada en entornos de desarrollo o testing</li>
            <li>Asegúrese de tener respaldos si los datos son importantes</li>
          </ul>
        </div>
      </div>
    </div>
  </div>

  <!-- Botones de Acción Iniciales -->
  <div *ngIf="!showSimulation && !showResetForm && !showResults" class="grid grid-cols-1 md:grid-cols-2 gap-4">
    
    <!-- Simular Reseteo -->
    <div class="bg-blue-50 border border-blue-200 rounded-lg p-6">
      <h3 class="text-lg font-semibold text-blue-800 mb-3">🔍 Simular Reseteo</h3>
      <p class="text-blue-700 mb-4">Ver qué datos serán eliminados sin ejecutar la acción real</p>
      <button 
        (click)="simulateReset()"
        class="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition">
        Simular Reseteo
      </button>
    </div>

    <!-- Ejecutar Reseteo -->
    <div class="bg-red-50 border border-red-200 rounded-lg p-6">
      <h3 class="text-lg font-semibold text-red-800 mb-3">🔥 Ejecutar Reseteo</h3>
      <p class="text-red-700 mb-4">Eliminar permanentemente todos los datos del sistema</p>
      <button 
        (click)="showResetFormMethod()"
        class="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition">
        Proceder con Reseteo
      </button>
    </div>
  </div>

  <!-- Simulación de Datos a Eliminar -->
  <div *ngIf="showSimulation && resetSummary" class="bg-white rounded-xl border border-gray-200 shadow-sm">
    <div class="p-6">
      <h2 class="text-xl font-bold text-gray-800 mb-4">📊 Simulación: Datos a Eliminar</h2>
      <p class="text-gray-600 mb-6">Los siguientes datos serían eliminados si se ejecuta el reseteo:</p>
      
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        
        <!-- Recibos e Ingresos -->
        <div class="bg-green-50 p-4 rounded-lg border border-green-200">
          <h4 class="font-semibold text-green-800 mb-3">💰 Ingresos y Recibos</h4>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span>Recibos de Ingreso:</span>
              <span class="font-medium">{{ formatNumber(resetSummary.incomeReceipts) }}</span>
            </div>
            <div class="flex justify-between">
              <span>Detalles de Comisiones:</span>
              <span class="font-medium">{{ formatNumber(resetSummary.incomeFeeDetails) }}</span>
            </div>
            <div class="flex justify-between">
              <span>Pagos en Exceso:</span>
              <span class="font-medium">{{ formatNumber(resetSummary.excessPayments) }}</span>
            </div>
          </div>
        </div>

        <!-- Planes de Pago -->
        <div class="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h4 class="font-semibold text-blue-800 mb-3">📋 Planes de Pago</h4>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span>Planes de Pago:</span>
              <span class="font-medium">{{ formatNumber(resetSummary.paymentPlans) }}</span>
            </div>
            <div class="flex justify-between">
              <span>Enlaces de Pago:</span>
              <span class="font-medium">{{ formatNumber(resetSummary.paymentLinks) }}</span>
            </div>
          </div>
        </div>

        <!-- Inscripciones -->
        <div class="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <h4 class="font-semibold text-purple-800 mb-3">👥 Inscripciones</h4>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span>Inscripciones:</span>
              <span class="font-medium">{{ formatNumber(resetSummary.enrollments) }}</span>
            </div>
            <div class="flex justify-between">
              <span>Participantes Corp.:</span>
              <span class="font-medium">{{ formatNumber(resetSummary.corporateEnrollmentParticipants) }}</span>
            </div>
          </div>
        </div>

        <!-- Gastos -->
        <div class="bg-red-50 p-4 rounded-lg border border-red-200">
          <h4 class="font-semibold text-red-800 mb-3">💸 Gastos</h4>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span>Vouchers de Gasto:</span>
              <span class="font-medium">{{ formatNumber(resetSummary.expenseVouchers) }}</span>
            </div>
            <div class="flex justify-between">
              <span>Items de Vouchers:</span>
              <span class="font-medium">{{ formatNumber(resetSummary.expenseVouchersItems) }}</span>
            </div>
            <div class="flex justify-between">
              <span>Detalles de Comisiones:</span>
              <span class="font-medium">{{ formatNumber(resetSummary.expenseFeeDetails) }}</span>
            </div>
          </div>
        </div>

        <!-- Órdenes de Trabajo -->
        <div class="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <h4 class="font-semibold text-yellow-800 mb-3">🔧 Órdenes de Trabajo</h4>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span>Órdenes de Trabajo:</span>
              <span class="font-medium">{{ formatNumber(resetSummary.workOrders) }}</span>
            </div>
            <div class="flex justify-between">
              <span>Items de Órdenes:</span>
              <span class="font-medium">{{ formatNumber(resetSummary.workOrderItems) }}</span>
            </div>
          </div>
        </div>

        <!-- Total -->
        <div class="bg-gray-100 p-4 rounded-lg border-2 border-gray-400">
          <h4 class="font-bold text-gray-800 mb-3">📊 TOTAL</h4>
          <div class="text-center">
            <span class="text-2xl font-bold text-gray-800">{{ formatNumber(resetSummary.totalRecords) }}</span>
            <p class="text-sm text-gray-600">registros serán eliminados</p>
          </div>
        </div>
      </div>

      <!-- Botón para proceder -->
      <div class="mt-6 text-center">
        <button 
          (click)="showResetFormMethod()"
          class="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition font-semibold">
          Proceder con el Reseteo Real
        </button>
      </div>
    </div>
  </div>

  <!-- Formulario de Reseteo Real -->
  <div *ngIf="showResetForm" class="bg-white rounded-xl border border-red-300 shadow-lg">
    <div class="p-6">
      <h2 class="text-xl font-bold text-red-800 mb-4">🔑 Confirmación de Reseteo</h2>
      
      <div class="bg-red-100 border border-red-300 rounded-lg p-4 mb-6">
        <p class="text-red-800 font-medium mb-2">Para ejecutar el reseteo, debe ingresar la clave de confirmación:</p>
        <code class="bg-red-200 px-2 py-1 rounded text-red-900 font-mono">RESET_FINANCIAL_SYSTEM_2025</code>
      </div>

      <form [formGroup]="resetForm" (ngSubmit)="executeReset()">
        <div class="mb-6">
          <label for="confirmationKey" class="block text-gray-700 font-bold mb-2">
            Clave de Confirmación
          </label>
          <input
            type="text"
            id="confirmationKey"
            formControlName="confirmationKey"
            class="w-full px-3 py-2 border rounded-md font-mono"
            [ngClass]="{'border-red-500': hasError('confirmationKey', 'required')}"
            placeholder="Ingrese la clave de confirmación"
          />
          <div class="text-red-500 text-sm mt-1" *ngIf="hasError('confirmationKey', 'required')">
            La clave de confirmación es obligatoria
          </div>
        </div>

        <!-- Opción de Dry Run -->
        <div class="mb-6">
          <label class="flex items-center">
            <input 
              type="checkbox" 
              formControlName="dryRun"
              class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
            <span class="ml-2 text-sm font-medium text-gray-700">
              🧪 Modo de Prueba (Dry Run) - Solo simular, no eliminar datos reales
            </span>
          </label>
          <p class="text-xs text-gray-500 mt-1 ml-6">
            Recomendado: Active esta opción primero para ver qué pasaría sin eliminar datos reales
          </p>
        </div>

        <!-- Resumen si está disponible -->
        <div *ngIf="resetSummary" class="bg-gray-50 border rounded-lg p-4 mb-6">
          <h4 class="font-semibold text-gray-800 mb-2">Resumen de eliminación:</h4>
          <p class="text-gray-700">Se eliminarán <strong>{{ formatNumber(resetSummary.totalRecords) }}</strong> registros del sistema</p>
        </div>

        <div class="flex justify-end gap-4">
          <button
            type="button"
            (click)="resetView()"
            class="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
            Cancelar
          </button>
          <button
            type="submit"
            [disabled]="resetForm.invalid || isResetting"
            [ngClass]="{
              'bg-blue-600 text-white hover:bg-blue-700': !resetForm.invalid && !isResetting && resetForm.get('dryRun')?.value,
              'bg-red-600 text-white hover:bg-red-700': !resetForm.invalid && !isResetting && !resetForm.get('dryRun')?.value,
              'bg-gray-400 text-gray-700 cursor-not-allowed': resetForm.invalid || isResetting
            }"
            class="px-6 py-2 rounded-md font-semibold transition">
            <span *ngIf="!isResetting">
              {{ resetForm.get('dryRun')?.value ? '🧪 EJECUTAR SIMULACIÓN' : '🔥 EJECUTAR RESETEO' }}
            </span>
            <span *ngIf="isResetting">
              {{ resetForm.get('dryRun')?.value ? 'Simulando...' : 'Reseteando...' }}
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Resultados del Reseteo -->
  <div *ngIf="showResults && resetResponse" class="bg-white rounded-xl border border-green-300 shadow-lg">
    <div class="p-6">
      <div class="flex items-center mb-4">
        <svg class="w-8 h-8 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
        <h2 class="text-xl font-bold text-green-800">✅ Reseteo Completado</h2>
      </div>
      
      <div class="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
        <p class="text-green-800 font-medium">{{ resetResponse.message }}</p>
        <div class="mt-2 text-sm text-green-700">
          <p><strong>Tiempo de ejecución:</strong> {{ resetResponse.executionTime }}</p>
          <p><strong>Ejecutado en:</strong> {{ resetResponse.executedAt | date:'dd/MM/yyyy HH:mm:ss' }}</p>
        </div>
      </div>

      <!-- Detalles de eliminación -->
      <div *ngIf="resetResponse.details && resetResponse.details.length > 0" class="mb-4">
        <h4 class="font-semibold text-gray-800 mb-3">📋 Detalles de Eliminación</h4>
        <div class="bg-gray-50 rounded-lg p-4">
          <ul class="text-sm space-y-1">
            <li *ngFor="let detail of resetResponse.details" class="flex items-center">
              <svg class="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              {{ detail }}
            </li>
          </ul>
        </div>
      </div>

      <!-- Advertencias si las hay -->
      <div *ngIf="resetResponse.warnings && resetResponse.warnings.length > 0" class="mb-4">
        <h4 class="font-semibold text-yellow-800 mb-3">⚠️ Advertencias</h4>
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <ul class="text-sm space-y-1">
            <li *ngFor="let warning of resetResponse.warnings" class="flex items-center text-yellow-800">
              <svg class="w-4 h-4 text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              {{ warning }}
            </li>
          </ul>
        </div>
      </div>

      <div class="text-center pt-4">
        <button 
          (click)="resetView()"
          class="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition font-semibold">
          Listo
        </button>
      </div>
    </div>
  </div>

</div>
